/** SUNTRONIX ONE — Smart Format Bulk (Product Master admin).
 *  Governed Analyse → Preview → Review → Confirm → Apply → Rollback workflow.
 *  FORMAT-ONLY: inserts selective **bold** into Product Master descriptions
 *  without changing any specification wording (verified server-side). Admin only. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, Loader2, Undo2, History, Search, X, Check, ChevronRight } from "lucide-react";
import { renderBoldPreview } from "@/components/commercial/BoldableTextarea";

const CHUNK = 100;
const STATUS_TONE = {
  ELIGIBLE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  MANUAL_FORMATTING: "bg-violet-50 text-violet-700 border-violet-200",
  REVIEW_REQUIRED: "bg-amber-50 text-amber-800 border-amber-200",
  NO_CHANGE: "bg-slate-50 text-slate-500 border-slate-200",
  NO_DESCRIPTION: "bg-slate-50 text-slate-400 border-slate-200",
};

export default function SmartFormatBulk({
  presetIds = null, trigger = null, onDone,
  headless = false, open: openProp, onOpenChange,
}) {
  const [iOpen, setIOpen] = useState(false);
  const open = openProp !== undefined ? openProp : iOpen;
  const setOpen = onOpenChange || setIOpen;
  const [status, setStatus] = useState(null);   // dashboard; also gates admin
  const [step, setStep] = useState("analyse");   // analyse | preview | confirm | applying | done | history
  const [scope, setScope] = useState({ brand: "", category: "" });
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState({ q: "", status: "ELIGIBLE" });
  const [page, setPage] = useState({ items: [], matched: 0, skip: 0, limit: 25 });
  const [approved, setApproved] = useState({});  // id -> bool
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);

  const loadStatus = useCallback(() => {
    api.get("/descriptions/smart-format/status")
      .then((r) => setStatus(r.data)).catch(() => setStatus(false));
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Initialise wizard whenever it opens (works for both controlled + uncontrolled).
  useEffect(() => {
    if (!open) return;
    setResult(null); setApproved({});
    if (presetIds && presetIds.length) { setStep("preview"); loadSelected(); }
    else { setStep("analyse"); setAnalysis(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A standalone (non-headless) "All" card is hidden from non-admins.
  if (status === false && !presetIds && !headless) return null;

  const openWizard = () => setOpen(true);

  const runAnalyse = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/descriptions/smart-format/analyse", {
        brand: scope.brand || undefined, category: scope.category || undefined });
      setAnalysis(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Analyse failed"); }
    setBusy(false);
  };

  const loadPage = async (skip = 0) => {
    setBusy(true);
    try {
      const { data } = await api.post("/descriptions/smart-format/preview", {
        brand: scope.brand || undefined, category: scope.category || undefined,
        q: filters.q || undefined, status: filters.status || undefined,
        skip, limit: 25 });
      setPage(data);
      const next = { ...approved };
      data.items.forEach((i) => { if (i.status === "ELIGIBLE" && next[i.id] === undefined) next[i.id] = true; });
      setApproved(next);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    setBusy(false);
  };

  const loadSelected = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/descriptions/smart-format/bulk-preview", { product_ids: presetIds });
      const items = data.items.map((i) => ({ ...i, status: i.changed ? "ELIGIBLE" : "NO_CHANGE" }));
      setPage({ items, matched: items.length, skip: 0, limit: items.length });
      const next = {}; items.forEach((i) => { if (i.status === "ELIGIBLE") next[i.id] = true; });
      setApproved(next);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    setBusy(false);
  };

  const goPreview = () => { setStep("preview"); loadPage(0); };
  const approveAllVisible = () => {
    const next = { ...approved };
    page.items.forEach((i) => { if (i.status === "ELIGIBLE") next[i.id] = true; });
    setApproved(next);
  };
  const approvedIds = Object.keys(approved).filter((k) => approved[k]);

  const runApply = async () => {
    if (!approvedIds.length) { toast.error("Approve at least one product"); return; }
    setStep("applying"); setProgress({ done: 0, total: approvedIds.length });
    let batchId = null;
    const agg = { updated: 0, skipped: 0, review_required: 0, failed: 0, batch_id: null };
    try {
      for (let i = 0; i < approvedIds.length; i += CHUNK) {
        const chunk = approvedIds.slice(i, i + CHUNK);
        const { data } = await api.post("/descriptions/smart-format/apply",
          { product_ids: chunk, batch_id: batchId, note: presetIds ? "Smart Format Selected" : "Smart Format All" });
        batchId = data.batch_id; agg.batch_id = data.batch_id;
        agg.updated += data.updated; agg.skipped += data.skipped;
        agg.review_required += data.review_required; agg.failed += data.failed;
        setProgress({ done: Math.min(i + CHUNK, approvedIds.length), total: approvedIds.length });
      }
      setResult(agg); setStep("done"); loadStatus(); onDone?.();
      toast.success(`Batch ${agg.batch_id}: ${agg.updated} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed"); setStep("preview");
    }
  };

  const loadBatches = async () => {
    setStep("history"); setBusy(true);
    try { const { data } = await api.get("/descriptions/smart-format/batches"); setBatches(data.items || []); }
    catch { /* */ }
    setBusy(false);
  };
  const rollback = async (bid) => {
    if (!window.confirm(`Roll back batch ${bid}? This restores the original descriptions changed by this batch only.`)) return;
    try {
      const { data } = await api.post(`/descriptions/smart-format/rollback/${bid}`);
      toast.success(`Rolled back ${data.reverted} product(s)`); loadBatches(); loadStatus(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Rollback failed"); }
  };

  return (
    <>
      {headless ? null : trigger
        ? <span onClick={openWizard}>{trigger}</span>
        : (
          <button onClick={openWizard} data-testid="smart-format-all-open"
            className="border border-indigo-200 rounded-xl bg-white px-3 py-2.5 text-left hover:border-indigo-300 transition-colors min-w-[240px]">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-700">
              <Sparkles className="h-3.5 w-3.5" /> Smart Format
            </div>
            {status && typeof status === "object" ? (
              <div className="mt-1 text-[11px] text-slate-500 grid grid-cols-2 gap-x-3">
                <span>Formatted: <b className="text-slate-700">{status.formatted}</b></span>
                <span>Manual: <b className="text-slate-700">{status.manual_formatting}</b></span>
                <span className="col-span-2">Last batch: <b className="text-slate-700">{status.last_batch?.batch_id || "—"}</b></span>
              </div>
            ) : <div className="mt-1 text-[11px] text-slate-400">Bulk-emphasise key specs across the catalogue</div>}
          </button>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="smart-format-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-indigo-600" />
              {presetIds ? "Smart Format Selected" : "Smart Format All"}
              <button onClick={loadBatches} title="History"
                className="ml-auto text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                data-testid="smart-format-history-btn"><History className="h-3.5 w-3.5" /> History</button>
            </DialogTitle>
            <DialogDescription className="text-[12px]">
              Format-only — adds selective bold to key specs (SKU, chip, RAM, storage, warranty…). It never changes
              wording, pricing, GST, HSN, SKU or stock. Review before applying.
            </DialogDescription>
          </DialogHeader>

          {/* STEP: ANALYSE */}
          {step === "analyse" && (
            <div className="space-y-3" data-testid="smart-format-analyse">
              <div className="flex items-center gap-2 flex-wrap">
                <input value={scope.brand} onChange={(e) => setScope({ ...scope, brand: e.target.value })}
                  placeholder="Brand (optional, e.g. Apple)" data-testid="sf-scope-brand"
                  className="h-9 border border-slate-200 rounded px-2 text-[13px]" />
                <input value={scope.category} onChange={(e) => setScope({ ...scope, category: e.target.value })}
                  placeholder="Category (optional)" data-testid="sf-scope-category"
                  className="h-9 border border-slate-200 rounded px-2 text-[13px]" />
                <Button size="sm" onClick={runAnalyse} disabled={busy} data-testid="sf-analyse-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />} Analyse
                </Button>
              </div>
              {analysis && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="sf-analysis">
                  {[["Scanned", analysis.counts.scanned], ["Eligible", analysis.counts.eligible],
                    ["Already formatted", analysis.counts.manual_formatting],
                    ["No change", analysis.counts.no_change], ["Review req.", analysis.counts.review_required]].map(([k, v]) => (
                    <div key={k} className="rounded border border-slate-200 bg-white py-2 text-center">
                      <div className="text-[10px] uppercase text-slate-400">{k}</div>
                      <div className="text-lg font-semibold text-slate-900">{v}</div>
                    </div>
                  ))}
                </div>
              )}
              {analysis && (
                <div className="flex justify-end">
                  <Button size="sm" onClick={goPreview} disabled={!analysis.counts.eligible}
                    data-testid="sf-goto-preview" className="bg-indigo-600 hover:bg-indigo-700">
                    Preview {analysis.counts.eligible} eligible <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* STEP: PREVIEW */}
          {step === "preview" && (
            <div className="space-y-2" data-testid="smart-format-preview">
              {!presetIds && (
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                    <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && loadPage(0)}
                      placeholder="Search name / SKU…" data-testid="sf-search"
                      className="h-8 pl-7 pr-2 border border-slate-200 rounded text-[12px] w-56" />
                  </div>
                  <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    data-testid="sf-filter-status"
                    className="h-8 border border-slate-200 rounded px-2 text-[12px] bg-white">
                    {["ELIGIBLE", "MANUAL_FORMATTING", "REVIEW_REQUIRED", "NO_CHANGE", ""].map((s) =>
                      <option key={s} value={s}>{s ? s.replace(/_/g, " ") : "All statuses"}</option>)}
                  </select>
                  <Button size="sm" variant="outline" onClick={() => loadPage(0)} data-testid="sf-apply-filters">Filter</Button>
                  <Button size="sm" variant="outline" onClick={approveAllVisible} data-testid="sf-approve-visible">Approve all visible</Button>
                  <span className="text-[11px] text-slate-500 ml-auto">{approvedIds.length} approved · {page.matched} match</span>
                </div>
              )}

              <div className="border border-slate-200 rounded-lg divide-y max-h-[46vh] overflow-y-auto">
                {page.items.length === 0 && (
                  <div className="text-center py-8 text-slate-400 text-[12px]">{busy ? "Loading…" : "No products match."}</div>
                )}
                {page.items.map((it) => (
                  <div key={it.id} className="p-2.5" data-testid={`sf-row-${it.id}`}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={!!approved[it.id]} disabled={it.status !== "ELIGIBLE"}
                        onChange={(e) => setApproved({ ...approved, [it.id]: e.target.checked })}
                        data-testid={`sf-approve-${it.id}`} />
                      <span className="text-[12px] font-medium text-slate-900 truncate flex-1">{it.name}</span>
                      <span className="text-[11px] font-mono text-slate-500">{it.code || "—"}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded border ${STATUS_TONE[it.status] || ""}`}>{(it.status || "").replace(/_/g, " ")}</span>
                    </div>
                    {it.status === "ELIGIBLE" && (
                      <div className="grid grid-cols-2 gap-2 mt-1.5">
                        <div className="text-[11px] text-slate-400 bg-slate-50 border border-slate-100 rounded px-2 py-1 leading-relaxed">
                          <span className="text-[8px] uppercase tracking-wide text-slate-300 block">Current</span>
                          {it.before}
                        </div>
                        <div className="text-[11px] text-slate-700 bg-indigo-50/40 border border-indigo-100 rounded px-2 py-1 leading-relaxed"
                          data-testid={`sf-after-${it.id}`}
                          dangerouslySetInnerHTML={{ __html: "<span class='text-[8px] uppercase tracking-wide text-indigo-300 block'>Smart formatted</span>" + renderBoldPreview(it.after) }} />
                      </div>
                    )}
                    {it.reason && <div className="text-[10px] text-amber-700 mt-1">{it.reason}</div>}
                  </div>
                ))}
              </div>

              {!presetIds && page.matched > page.limit && (
                <div className="flex items-center justify-between text-[11px]">
                  <Button size="sm" variant="ghost" disabled={page.skip === 0 || busy}
                    onClick={() => loadPage(Math.max(0, page.skip - 25))}>Prev</Button>
                  <span className="text-slate-500">{page.skip + 1}–{Math.min(page.skip + page.limit, page.matched)} of {page.matched}</span>
                  <Button size="sm" variant="ghost" disabled={page.skip + page.limit >= page.matched || busy}
                    onClick={() => loadPage(page.skip + 25)}>Next</Button>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => presetIds ? setOpen(false) : setStep("analyse")}>Back</Button>
                <Button onClick={() => setStep("confirm")} disabled={!approvedIds.length}
                  data-testid="sf-goto-confirm" className="bg-indigo-600 hover:bg-indigo-700">
                  Review &amp; apply ({approvedIds.length})
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* STEP: CONFIRM */}
          {step === "confirm" && (
            <div className="space-y-3 text-center py-2" data-testid="smart-format-confirm">
              <Sparkles className="h-8 w-8 mx-auto text-indigo-600" />
              <div className="text-[14px] text-slate-900">You are about to format
                <b> {approvedIds.length} product description(s)</b>.</div>
              <div className="text-[11px] text-slate-500 max-w-md mx-auto">
                This applies selective bold only. It will NOT change product name, SKU, part number, HSN, cost,
                price, GST, inventory, stock, brand, category, spec wording or legacy import identifiers. Existing quotations
                are untouched; new quotes inherit the formatted description.
              </div>
              <DialogFooter className="justify-center">
                <Button variant="outline" onClick={() => setStep("preview")}>Cancel</Button>
                <Button onClick={runApply} data-testid="sf-confirm-apply" className="bg-indigo-600 hover:bg-indigo-700">
                  Apply Smart Formatting
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* STEP: APPLYING */}
          {step === "applying" && (
            <div className="space-y-3 text-center py-6" data-testid="smart-format-applying">
              <Loader2 className="h-8 w-8 mx-auto text-indigo-600 animate-spin" />
              <div className="text-[13px] text-slate-700">Formatting {progress.done} / {progress.total} products…</div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden max-w-md mx-auto">
                <div className="h-full bg-indigo-600 transition-all"
                  style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
              </div>
            </div>
          )}

          {/* STEP: DONE */}
          {step === "done" && result && (
            <div className="space-y-3 text-center py-2" data-testid="smart-format-done">
              <Check className="h-9 w-9 mx-auto text-emerald-600" />
              <div className="text-[14px] font-medium text-slate-900">Batch {result.batch_id} complete</div>
              <div className="grid grid-cols-4 gap-2 max-w-lg mx-auto">
                {[["Updated", result.updated], ["Skipped", result.skipped],
                  ["Review", result.review_required], ["Failed", result.failed]].map(([k, v]) => (
                  <div key={k} className="rounded border border-slate-200 bg-white py-1.5">
                    <div className="text-[10px] uppercase text-slate-400">{k}</div>
                    <div className="text-lg font-semibold text-slate-900">{v}</div>
                  </div>
                ))}
              </div>
              <DialogFooter className="justify-center">
                <Button variant="outline" onClick={() => rollback(result.batch_id)} data-testid="sf-rollback-done">
                  <Undo2 className="h-4 w-4 mr-1" /> Undo this batch
                </Button>
                <Button onClick={() => setOpen(false)} className="bg-indigo-600 hover:bg-indigo-700">Done</Button>
              </DialogFooter>
            </div>
          )}

          {/* STEP: HISTORY */}
          {step === "history" && (
            <div className="space-y-2" data-testid="smart-format-history">
              <div className="border border-slate-200 rounded-lg divide-y max-h-[50vh] overflow-y-auto">
                {batches.length === 0 && <div className="text-center py-8 text-slate-400 text-[12px]">No batches yet.</div>}
                {batches.map((b) => (
                  <div key={b.batch_id} className="flex items-center gap-2 px-3 py-2 text-[12px]" data-testid={`sf-batch-${b.batch_id}`}>
                    <span className="font-mono font-semibold text-slate-800">{b.batch_id}</span>
                    <span className="text-slate-400">{(b.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    <span className="text-slate-500">{b.user}</span>
                    <span className="text-emerald-700">↑{b.counts?.updated ?? 0}</span>
                    <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded border ${b.status === "ROLLED_BACK" ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{b.status}</span>
                    {b.status !== "ROLLED_BACK" && (
                      <button onClick={() => rollback(b.batch_id)} data-testid={`sf-rollback-${b.batch_id}`}
                        className="text-[11px] text-rose-600 hover:text-rose-800 inline-flex items-center gap-1">
                        <Undo2 className="h-3 w-3" /> Rollback</button>
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStep(presetIds ? "preview" : "analyse")}>
                  <X className="h-4 w-4 mr-1" /> Close history
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
